const MIN_MATCH_CHARS = 3;
const MIN_FUZZY_CHARS = 5;

const TOKEN_ALIASES = {
  dw: ["dyeworks"],
  fg: ["foreground"],
  bg: ["background"]
};

function normalizeText(value = "") {
  return value
    .toLowerCase()
    .replace(/[:/\\|,_-]+/g, " ")
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(value = "") {
  const normalized = normalizeText(value);
  return normalized ? normalized.split(" ") : [];
}

function expandQueryTokens(tokens = []) {
  return tokens.flatMap((token) => TOKEN_ALIASES[token] || [token]);
}

function splitNameParts(name = "") {
  const [left, ...rest] = name.split(":");
  if (rest.length === 0) {
    return {
      modifier: "",
      baseName: name,
    };
  }

  return {
    modifier: left.trim(),
    baseName: rest.join(":").trim(),
  };
}

export function preprocessNames(names = []) {
  return names.map((name) => {
    const { modifier, baseName } = splitNameParts(name);
    const normalized = normalizeText(name);
    const baseNormalized = normalizeText(baseName);
    const modifierNormalized = normalizeText(modifier);

    return {
      original: name,
      normalized,
      tokens: tokenize(name),
      baseName,
      baseNormalized,
      baseTokens: tokenize(baseName),
      modifier,
      modifierNormalized,
      modifierTokens: tokenize(modifier),
    };
  });
}

function levenshtein(a = "", b = "") {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix = Array.from({ length: a.length + 1 }, () =>
    new Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i += 1) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j += 1) matrix[0][j] = j;

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );

      if (
        i > 1 &&
        j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1]
      ) {
        matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + cost);
      }
    }
  }

  return matrix[a.length][b.length];
}

function getTokenMatchScore(queryToken, itemTokens, allowFuzzy) {
  let bestScore = 0;
  let matched = false;
  let fuzzyMatched = false;

  for (const itemToken of itemTokens) {
    if (itemToken === queryToken) {
      return { score: 60, matched: true, fuzzyMatched: false };
    }

    if (itemToken.startsWith(queryToken)) {
      bestScore = Math.max(bestScore, 35);
      matched = true;
      continue;
    }

    if (itemToken.includes(queryToken)) {
      bestScore = Math.max(bestScore, 20);
      matched = true;
      continue;
    }

    if (
      allowFuzzy &&
      queryToken.length >= MIN_FUZZY_CHARS &&
      itemToken.length >= 4
    ) {
      const distance = levenshtein(queryToken, itemToken);
      const maxDistance = queryToken.length >= 8 ? 2 : 1;

      if (distance <= maxDistance) {
        const fuzzyScore = distance === 1 ? 14 : 8;
        if (fuzzyScore > bestScore) {
          bestScore = fuzzyScore;
          matched = true;
          fuzzyMatched = true;
        }
      }
    }
  }

  return { score: bestScore, matched, fuzzyMatched };
}

function scoreCandidate(query, expandedTokens, item) {
  let score = 0;
  let matchedTokens = 0;
  let fuzzyMatchedTokens = 0;

  if (item.normalized === query) {
    score += 200;
  }

  if (item.baseNormalized === query) {
    score += 160;
  }

  if (item.baseNormalized && item.baseNormalized.includes(query)) {
    score += 80;
  }

  if (item.normalized.startsWith(query)) {
    score += 70;
  }

  const allowFuzzy = query.length >= MIN_FUZZY_CHARS;

  for (const queryToken of expandedTokens) {
    const tokenResult = getTokenMatchScore(queryToken, item.tokens, allowFuzzy);

    if (tokenResult.matched) {
      matchedTokens += 1;
      score += tokenResult.score;

      if (tokenResult.fuzzyMatched) {
        fuzzyMatchedTokens += 1;
      }
    }
  }

  if (expandedTokens.length > 0 && matchedTokens === expandedTokens.length) {
    score += 120;
  }

  const missingTokens = expandedTokens.length - matchedTokens;
  score -= missingTokens * 25;
  score -= fuzzyMatchedTokens * 4;

  if (item.baseTokens.length > 0) {
    const baseExactMatches = expandedTokens.filter((token) =>
      item.baseTokens.includes(token)
    ).length;
    score += baseExactMatches * 12;
  }

  score -= item.original.length * 0.01;

  return {
    item,
    score,
    matchedTokens,
    fuzzyMatchedTokens,
  };
}

export function getSuggestions({
  query,
  indexedNames,
  maxSuggestions,
  minMatchChars = MIN_MATCH_CHARS,
}) {
  const normalizedQuery = normalizeText(query);

  if (normalizedQuery.length < minMatchChars) {
    return [];
  }

  const expandedTokens = expandQueryTokens(tokenize(normalizedQuery));

  const ranked = indexedNames
    .map((item) => scoreCandidate(normalizedQuery, expandedTokens, item))
    .filter((result) => result.score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.matchedTokens !== a.matchedTokens) {
        return b.matchedTokens - a.matchedTokens;
      }
      return a.item.original.localeCompare(b.item.original);
    })
    .slice(0, maxSuggestions);

  return ranked.map((result) => result.item.original);
}